import qs from 'qs';

/**
 * Wraps native `navigator.share` and adds an extra functionality. If method is
 * supported by browser, it will return wrapped method, otherwise `undefined`.
 *
 *
 * @typedef {Object} Share
 * @property {(((event?: Event) => (void|false|Promise<void|false>))|undefined)} handleShare - Share handler when supported; otherwise `undefined`.
 * @property {string} shareUrl - Android share url when supported; otherwise empty string.
 *
 * @param {Object} params
 * @param {Object} params.data Data to be passed to `navigator.share` as per https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share
 * @param {(() => (void|Promise<void>))} params.onShare Callback called only when content was successfully shared.
 * @returns {Share}
 */
const useNavigatorShare = ({ data, onShare }) => {
  // Don't forget to add 'android_share_handler' into the array at
  // app/assets/javascripts/initializers/frontoffice.js:22
  // ex : window.AlumniConnect.mobile = window.mobile_option || ['people_directory_map'];
  const androidShare = AlumniConnect?.mobile?.includes('android_share_handler');
  const canShare = !!navigator.share || androidShare;

  const shareData = {
    ...data,
    text: data?.text ? `${data.text}\n` : undefined,
  };
  const shareUrl = androidShare
    ? `hbshare://share?${qs.stringify(shareData)}`
    : '';

  const navigatorShare = async () => {
    try {
      await navigator.share(shareData);
      onShare?.();
    } catch {
      // Do nothing if sharing was unsuccessful
    }
  };

  const handleShare = canShare
    ? async (event) => {
        if (androidShare) {
          await onShare?.();

          return;
        }

        event?.preventDefault();
        event?.stopPropagation();
        navigatorShare();

        return false;
      }
    : undefined;

  return { handleShare, shareUrl };
};

export default useNavigatorShare;
